"use client";

import { c as _c } from "react/compiler-runtime";
import { useCurrentCityName } from "@/hooks/use-current-city";
import { getCityStats } from "@/info/seo/stats";
import { escapeHtml } from "@/utils/security";
import { formatBedrooms } from "@/utils/sup/get-meta-data";
import { usePathname } from "next/navigation";
import { memo, useMemo } from "react";
interface SeoBannerProps {
  // For project/unit pages
  projectName?: string;
  locality?: string;
  propertyTypes?: string[];
  availableUnitsCount?: number;
  availableBedrooms?: number[];
}

/**
 * Universal SEO banner component that displays dynamic statistics
 * Works for search pages, project pages, and unit pages
 * Detects route and shows appropriate content
 */
export const SEOContentForProjectOrSup = memo(function SEOContentForProjectOrSup(t0) {
  const $ = _c(68);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    projectName,
    locality: projectLocality,
    propertyTypes,
    availableUnitsCount,
    availableBedrooms
  } = t1;
  const pathname = usePathname();
  const city = useCurrentCityName();
  let t2;
  bb0: {
    if (pathname?.startsWith("/projects/")) {
      t2 = "project";
      break bb0;
    }
    if (pathname?.startsWith("/units/") || pathname?.startsWith("/unit/")) {
      t2 = "unit";
      break bb0;
    }
    t2 = "search";
  }
  const pageType = t2;
  let bedroomLabel;
  let cityDisplay;
  let formattedListings;
  let isVillaSearch;
  let pluralLabel;
  let resalePropertyType;
  let singularLabel;
  let stats;
  let t3;
  if ($[2] !== availableBedrooms || $[3] !== availableUnitsCount || $[4] !== city || $[5] !== pageType || $[6] !== propertyTypes) {
    stats = getCityStats(city);
    let t4;
    if ($[16] !== city) {
      t4 = city.charAt(0).toUpperCase();
      $[16] = city;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    const t5 = t4 + city.slice(1);
    let t6;
    if ($[18] !== t5) {
      t6 = escapeHtml(t5);
      $[18] = t5;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    cityDisplay = t6;
    const isGenericSearch = !propertyTypes || propertyTypes.length === 0;
    let t7;
    bb1: {
      if (isGenericSearch) {
        let t8;
        if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = {
            isVillaSearch: false,
            singularLabel: "flats and houses",
            pluralLabel: "apartments and villas",
            resalePropertyType: "properties"
          };
          $[20] = t8;
        } else {
          t8 = $[20];
        }
        t7 = t8;
        break bb1;
      }
      let t8;
      if ($[21] !== propertyTypes) {
        t8 = propertyTypes?.some(_temp) || false;
        $[21] = propertyTypes;
        $[22] = t8;
      } else {
        t8 = $[22];
      }
      const isVilla = t8;
      const t9 = isVilla ? "houses" : pageType === "project" ? "flats" : "flat";
      const t10 = isVilla ? "villas" : "apartments";
      const t11 = isVilla ? "house" : "apartment";
      let t12;
      if ($[23] !== isVilla || $[24] !== t10 || $[25] !== t11 || $[26] !== t9) {
        t12 = {
          isVillaSearch: isVilla,
          singularLabel: t9,
          pluralLabel: t10,
          resalePropertyType: t11
        };
        $[23] = isVilla;
        $[24] = t10;
        $[25] = t11;
        $[26] = t9;
        $[27] = t12;
      } else {
        t12 = $[27];
      }
      t7 = t12;
    }
    ({
      isVillaSearch,
      singularLabel,
      pluralLabel,
      resalePropertyType
    } = t7);
    let t8;
    if ($[28] !== availableBedrooms) {
      t8 = Array.isArray(availableBedrooms) ? formatBedrooms(availableBedrooms) : "";
      $[28] = availableBedrooms;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    bedroomLabel = t8;
    formattedListings = availableUnitsCount;
    t3 = stats.yearlyVisits.toLocaleString();
    $[2] = availableBedrooms;
    $[3] = availableUnitsCount;
    $[4] = city;
    $[5] = pageType;
    $[6] = propertyTypes;
    $[7] = bedroomLabel;
    $[8] = cityDisplay;
    $[9] = formattedListings;
    $[10] = isVillaSearch;
    $[11] = pluralLabel;
    $[12] = resalePropertyType;
    $[13] = singularLabel;
    $[14] = stats;
    $[15] = t3;
  } else {
    bedroomLabel = $[7];
    cityDisplay = $[8];
    formattedListings = $[9];
    isVillaSearch = $[10];
    pluralLabel = $[11];
    resalePropertyType = $[12];
    singularLabel = $[13];
    stats = $[14];
    t3 = $[15];
  }
  const formattedVisits = t3;
  const formattedBuyers = stats.buyersInLakhs;
  let t4;
  bb2: {
    const t5 = projectName || "";
    let t6;
    if ($[30] !== t5) {
      t6 = escapeHtml(t5);
      $[30] = t5;
      $[31] = t6;
    } else {
      t6 = $[31];
    }
    const safeProjectName = t6;
    const t7 = projectLocality || "";
    let t8;
    if ($[32] !== t7) {
      t8 = escapeHtml(t7);
      $[32] = t7;
      $[33] = t8;
    } else {
      t8 = $[33];
    }
    const safeLocality = t8;
    if (pageType === "project") {
      const t9 = safeProjectName || cityDisplay;
      const t10 = safeLocality || cityDisplay;
      const t11 = safeLocality || cityDisplay;
      let t12;
      if ($[34] !== t10 || $[35] !== t11 || $[36] !== t9) {
        t12 = {
          primaryLocation: t9,
          secondaryLocation: t10,
          finalLocation: t11
        };
        $[34] = t10;
        $[35] = t11;
        $[36] = t9;
        $[37] = t12;
      } else {
        t12 = $[37];
      }
      t4 = t12;
      break bb2;
    }
    const t9 = safeProjectName || safeLocality || cityDisplay;
    const t10 = safeProjectName && safeLocality ? safeLocality : safeProjectName || safeLocality || cityDisplay;
    const t11 = safeLocality || cityDisplay;
    let t12;
    if ($[38] !== t10 || $[39] !== t11 || $[40] !== t9) {
      t12 = {
        primaryLocation: t9,
        secondaryLocation: t10,
        finalLocation: t11
      };
      $[38] = t10;
      $[39] = t11;
      $[40] = t9;
      $[41] = t12;
    } else {
      t12 = $[41];
    }
    t4 = t12;
  }
  const {
    primaryLocation,
    secondaryLocation,
    finalLocation
  } = t4;
  let t5;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      unitPage: _temp2,
      projectPage: _temp3,
      searchPage: _temp4
    };
    $[42] = t5;
  } else {
    t5 = $[42];
  }
  const contentGenerators = t5;
  const hasProject = !!projectName;
  let t6;
  if ($[43] !== bedroomLabel || $[44] !== cityDisplay || $[45] !== finalLocation || $[46] !== hasProject || $[47] !== isVillaSearch || $[48] !== pageType || $[49] !== pluralLabel || $[50] !== primaryLocation || $[51] !== resalePropertyType || $[52] !== secondaryLocation || $[53] !== singularLabel) {
    const params_2 = {
      isVillaSearch,
      singularLabel,
      pluralLabel,
      bedroomLabel,
      resalePropertyType,
      primaryLocation,
      secondaryLocation,
      finalLocation,
      cityDisplay,
      hasProject
    };
    t6 = contentGenerators[pageType === "unit" ? "unitPage" : pageType === "project" ? "projectPage" : "searchPage"](params_2);
    $[43] = bedroomLabel;
    $[44] = cityDisplay;
    $[45] = finalLocation;
    $[46] = hasProject;
    $[47] = isVillaSearch;
    $[48] = pageType;
    $[49] = pluralLabel;
    $[50] = primaryLocation;
    $[51] = resalePropertyType;
    $[52] = secondaryLocation;
    $[53] = singularLabel;
    $[54] = t6;
  } else {
    t6 = $[54];
  }
  const content = t6;
  let t7;
  let t8;
  let t9;
  if ($[55] !== content.text || $[56] !== formattedBuyers || $[57] !== formattedListings || $[58] !== formattedVisits) {
    const finalText = content.text.replace("{count}", `<span class="font-semibold text-white">${formattedListings}+</span>`).replace("{visits}", `<span class="font-semibold text-white">${formattedVisits}</span>`).replace("{buyers}", `<span class="font-semibold text-white">${formattedBuyers}</span>`).replace("{sellers}", "<span class=\"font-semibold text-white\">30,000+</span>").replace("{totalBuyers}", "<span class=\"font-semibold text-white\">400,000+</span>");
    t9 = "container mx-auto px-4 py-8 md:py-12";
    t7 = "text-white leading-relaxed text-center text-sm md:text-base space-y-4";
    t8 = finalText.split("\n\n").map(_temp5);
    $[55] = content.text;
    $[56] = formattedBuyers;
    $[57] = formattedListings;
    $[58] = formattedVisits;
    $[59] = t7;
    $[60] = t8;
    $[61] = t9;
  } else {
    t7 = $[59];
    t8 = $[60];
    t9 = $[61];
  }
  let t10;
  if ($[62] !== t7 || $[63] !== t8) {
    t10 = <div className={t7}>{t8}</div>;
    $[62] = t7;
    $[63] = t8;
    $[64] = t10;
  } else {
    t10 = $[64];
  }
  let t11;
  if ($[65] !== t10 || $[66] !== t9) {
    t11 = <div className={t9}>{t10}</div>;
    $[65] = t10;
    $[66] = t9;
    $[67] = t11;
  } else {
    t11 = $[67];
  }
  return t11;
});
function _temp(type) {
  return type.toLowerCase().includes("villa") || type.toLowerCase().includes("house");
}
function _temp2(params) {
  const propertyTypeFirst = params.isVillaSearch ? "house and villas" : "flat and apartments";
  const propertyTypeLast = params.isVillaSearch ? "houses and villas" : "flats and apartments";
  return {
    text: `Find your perfect ${params.bedroomLabel ? `${params.bedroomLabel} ` : ""}${propertyTypeFirst} in ${params.primaryLocation} with the city's leading real estate broker. With over 10 years of experience in the real estate in ${params.cityDisplay} market, we have built a trusted platform that connects buyers and sellers seamlessly through innovation, transparency, and reliability.

Join a growing community of {sellers} verified sellers and {totalBuyers} buyers who have found their dream properties in ${params.finalLocation} with us. Whether you're looking for an affordable flat or a luxury apartment, our curated listings are verified for authenticity and tailored to fit every budget and lifestyle.

What sets us apart is our zero brokerage promise, without any subscription fee, helping buyers and sellers save more while enjoying expert guidance from one of the most experienced brokers in ${params.cityDisplay}. You can visit the properties directly via our platform. Our technology-driven tools make searching, comparing, and connecting easier than ever, while our dedicated team ensures a smooth, transparent transaction every step of the way. We also provide legal verification and documentation services, completely free of cost.

With millions of visits conducted every year, our platform continues to redefine how real estate in ${params.cityDisplay} is bought and sold. Explore trusted resale ${params.bedroomLabel ? `${params.bedroomLabel} ` : ""}${propertyTypeLast} in ${params.primaryLocation} and experience the convenience, confidence, and savings that come with choosing ${params.finalLocation}'s most reliable real estate broker, with zero brokerage guaranteed.`,
    positionClass: ""
  };
}
function _temp3(params_0) {
  const propertyTypeFirst_0 = params_0.isVillaSearch ? "house and villas" : "flat and apartments";
  const propertyTypeLast_0 = params_0.isVillaSearch ? "houses and villas" : "flats and apartments";
  return {
    text: `Find your perfect ${params_0.bedroomLabel ? `${params_0.bedroomLabel} ` : ""}${propertyTypeFirst_0} in ${params_0.primaryLocation} with the city's leading real estate broker. With over 10 years of experience in the real estate in ${params_0.cityDisplay} market, we have built a trusted platform that connects buyers and sellers seamlessly through innovation, transparency, and reliability.

Join a growing community of {sellers} verified sellers and {totalBuyers} buyers who have found their dream properties in ${params_0.finalLocation} with us. Whether you're looking for an affordable flat or a luxury apartment, our curated listings are verified for authenticity and tailored to fit every budget and lifestyle.

What sets us apart is our zero brokerage promise, without any subscription fee, helping buyers and sellers save more while enjoying expert guidance from one of the most experienced brokers in ${params_0.cityDisplay}. You can visit the properties directly via our platform. Our technology-driven tools make searching, comparing, and connecting easier than ever, while our dedicated team ensures a smooth, transparent transaction every step of the way. We also provide legal verification and documentation services, completely free of cost.

With millions of visits conducted every year, our platform continues to redefine how real estate in ${params_0.cityDisplay} is bought and sold. Explore trusted resale ${params_0.bedroomLabel ? `${params_0.bedroomLabel} ` : ""}${propertyTypeLast_0} in ${params_0.primaryLocation} and experience the convenience, confidence, and savings that come with choosing ${params_0.finalLocation}'s most reliable real estate broker, with zero brokerage guaranteed.`,
    positionClass: ""
  };
}
function _temp4(params_1) {
  if (params_1.hasProject) {
    return {
      text: `Find your perfect ${params_1.bedroomLabel ? `${params_1.bedroomLabel} ` : ""}${params_1.bedroomLabel ? "flat and apartments" : "home from {count} verified flats"} in ${params_1.primaryLocation} with the city's leading real estate broker. With over 10 years of experience in the real estate in ${params_1.cityDisplay} market, we have built a trusted platform that connects buyers and sellers seamlessly through innovation, transparency, and reliability.

Join a growing community of {sellers} verified sellers and {totalBuyers} buyers who have found their dream properties in ${params_1.finalLocation} with us. Whether you're looking for an affordable flat or a luxury apartment, our curated listings are verified for authenticity and tailored to fit every budget and lifestyle.

What sets us apart is our zero brokerage promise, without any subscription fee, helping buyers and sellers save more while enjoying expert guidance from one of the most experienced brokers in ${params_1.cityDisplay}. You can visit the properties directly via our platform. Our technology-driven tools make searching, comparing, and connecting easier than ever, while our dedicated team ensures a smooth, transparent transaction every step of the way. We also provide legal verification and documentation services, completely free of cost.

With millions of visits conducted every year, our platform continues to redefine how real estate in ${params_1.cityDisplay} is bought and sold. Explore trusted resale ${params_1.bedroomLabel ? `${params_1.bedroomLabel} flats and apartments` : "flats, apartments, houses, and villas"} in ${params_1.primaryLocation} and experience the convenience, confidence, and savings that come with choosing ${params_1.finalLocation}'s most reliable real estate broker, with zero brokerage guaranteed.`,
      positionClass: ""
    };
  }
  if (params_1.primaryLocation !== params_1.cityDisplay) {
    const firstProperty = params_1.isVillaSearch ? "houses" : "flats";
    const secondProperty = params_1.isVillaSearch ? "villas" : "apartments";
    return {
      text: `Find your perfect ${params_1.bedroomLabel ? `${params_1.bedroomLabel} ` : ""}home from {count} verified ${firstProperty} in ${params_1.primaryLocation}, premium ${params_1.bedroomLabel ? `${params_1.bedroomLabel} ` : ""}${secondProperty} in ${params_1.primaryLocation}, and exclusive villas with the city's leading real estate broker. With over 10 years of experience in the real estate in ${params_1.cityDisplay} market, we have built a trusted platform that connects buyers and sellers seamlessly through innovation, transparency, and reliability.

Join a growing community of {sellers} verified sellers and {totalBuyers} buyers who have found their dream properties in ${params_1.cityDisplay} with us. Whether you're looking for an affordable flat or a luxury apartment, our curated listings are verified for authenticity and tailored to fit every budget and lifestyle.

What sets us apart is our zero brokerage promise, without any subscription fee, helping buyers and sellers save more while enjoying expert guidance from one of the most experienced brokers in ${params_1.cityDisplay}. You can visit the properties directly via our platform. Our technology-driven tools make searching, comparing, and connecting easier than ever, while our dedicated team ensures a smooth, transparent transaction every step of the way. We also provide legal verification and documentation services, completely free of cost.

With millions of visits conducted every year, our platform continues to redefine how real estate in ${params_1.cityDisplay} is bought and sold. Explore trusted resale ${params_1.bedroomLabel ? `${params_1.bedroomLabel} ` : ""}flats, apartments, houses, and villas in ${params_1.primaryLocation}, and experience the convenience, confidence, and savings that come with choosing ${params_1.cityDisplay}'s most reliable real estate broker, with zero brokerage guaranteed.`,
      positionClass: ""
    };
  }
  const firstProperty_0 = params_1.isVillaSearch ? "houses" : "flats";
  const secondProperty_0 = params_1.isVillaSearch ? "villas" : "apartments";
  return {
    text: `Find your perfect home from {count} verified ${firstProperty_0} in ${params_1.cityDisplay}, premium ${secondProperty_0} in ${params_1.cityDisplay}, and exclusive villas with the city's leading real estate broker. With over 10 years of experience in the real estate in ${params_1.cityDisplay} market, we have built a trusted platform that connects buyers and sellers seamlessly through innovation, transparency, and reliability.

Join a growing community of {sellers} verified sellers and {totalBuyers} buyers who have found their dream properties in ${params_1.cityDisplay} with us. Whether you're looking for an affordable flat or a luxury apartment, our curated listings are verified for authenticity and tailored to fit every budget and lifestyle.

What sets us apart is our zero brokerage promise, without any subscription fee, helping buyers and sellers save more while enjoying expert guidance from one of the most experienced brokers in ${params_1.cityDisplay}. You can visit the properties directly via our platform. Our technology-driven tools make searching, comparing, and connecting easier than ever, while our dedicated team ensures a smooth, transparent transaction every step of the way. We also provide legal verification and documentation services, completely free of cost.

With millions of visits conducted every year, our platform continues to redefine how real estate in ${params_1.cityDisplay} is bought and sold. Explore trusted resale flats, apartments, houses, and villas in ${params_1.cityDisplay}, and experience the convenience, confidence, and savings that come with choosing ${params_1.cityDisplay}'s most reliable real estate broker, with zero brokerage guaranteed.`,
    positionClass: ""
  };
}
function _temp5(paragraph, index) {
  return <p key={index} dangerouslySetInnerHTML={{
    __html: paragraph
  }} />;
}